<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-shop me-2"></i>Store Purchase Orders
      </h4>
      <div class="d-flex">
        <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary"
            @click="orderModal = true; orderMode = 'add'">
          <i class="bi bi-plus-circle me-1"></i> Add Store Purchase Order
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading orders...</p>
      </div>

      <!-- Orders Content -->
      <div v-if="!loading" class="orders-content fade-in">
        <!-- Orders Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ orders.total }}</span>
            <span class="text-muted">Orders</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search order" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Orders Table -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>CODE</th>
                  <th>STORE</th>
                  <th>BY USER</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(order, index) in orders.data" :key="order.id" class="order-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ order.id.split("-").pop() }}</td>
                  <td>{{ order.code }}</td>
                  <td>{{ order.store.code }} - {{ order.store.name }}</td>
                  <td>{{ order.user.first_name }} {{ order.user.last_name }}</td>
                  <td>
                    <Badge pill :type="getPillByStatus(order.status)">
                      {{ order.status }}
                    </Badge>
                  </td>
                  <td>{{ formatDate(order.created_at) }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary" @click="orderDetails(order.id)">
                      <i class="bi bi-eye"></i> Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <!-- Add Order Modal -->
    <Modal :open="orderModal" @closed="orderModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0" v-if="orderMode === 'add'">
          <i class="bi bi-plus-circle me-2"></i>Add Store Purchase Order
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2"></i>Edit Store Purchase Order
        </h5>
        <button @click="orderModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="col-md-12">
              <div class="form-group mb-3">
                <label for="code" class="form-label fw-bold">Order Code:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-upc"></i></span>
                  <input type="text" class="form-control" placeholder="Code" v-model="order.code" readonly>
                </div>
                <small class="form-text text-muted">Auto-generated code</small>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="orderModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" v-if="orderMode === 'add'" @click="createOrder">
          <i class="bi bi-plus-circle me-1"></i> Create Order
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateOrder(order.id)">
          <i class="bi bi-check-circle me-1"></i> Update Order
        </button>
      </div>
    </Modal>
    <!-- Add Order Modal End -->
  </Card>
</template>

<script>
import storeOrderService from "@/api/storeOrderService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import formatDate from "@/utils/formatDate";

export default {
  name: "Orders",

  data(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: {},
      store: {},
      profile: {},
      loading: true,
      order: {
        organization_id: this.organization.id,
        store_id: this.store.id,
        user_id: this.profile.id,
      },
      orderMode: 'add',
      orderModal: false,
      dataTableHeaders: [
        '#',
        'Code',
        'Store',
        'By User',
        'Status',
        'Date',
        null
      ],
      orders: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      _searchDebounce: null,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    formatDate,
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'pending':
          return 'warning';
        case 'complete':
          return 'success';
      }
    },
    async getOrders() {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      this.orders = await storeOrderService.getOrders(this.store.id, params)
      const nextNo = await storeOrderService.getOrderNextNo()
      this.order.code = nextNo.next
      this.loading = false
    },
    orderDetails(id) {
      router.push('/store-order/' + id)
    },
    async createOrder() {
      let response = await storeOrderService.createOrder(this.order)
      if (response) {
        this.orderModal = false
        await this.getOrders()
      }
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getOrders()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getOrders()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
  },

  computed: {
    totalPages(){
      return this.orders?.last_page || 1
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getOrders()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getOrders()
    },
  },

  created: async function () {
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    await this.getOrders()
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

}
</script>

<style scoped>
/* Orders page styles */
.orders-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.order-row {
  transition: all 0.2s ease;
}

.order-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
